(()=>{
'use strict';
const DAY = 86400000;
const isPlain = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const finiteInt = (n, max = 366) => Number.isInteger(n) && n >= 0 && n <= max;
function civilDate(instant, timeZone) {
  const date = new Date(instant);
  if (!Number.isFinite(date.getTime())) throw new TypeError('Invalid clock');
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(date);
  const p = Object.fromEntries(parts.map(({type, value}) => [type, value]));
  return { date: `${p.year}-${p.month}-${p.day}`, minute: Number(p.hour) * 60 + Number(p.minute) };
}
function validDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(value + 'T00:00:00Z');
  return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === value;
}
function addCalendarDays(value, count) {
  if (!validDate(value) || !finiteInt(count, 1500)) throw new TypeError('Invalid date arithmetic');
  return new Date(new Date(value + 'T00:00:00Z').getTime() + count * DAY).toISOString().slice(0, 10);
}
function calendarValid(calendar) {
  return isPlain(calendar) && Array.isArray(calendar.weekdays) && calendar.weekdays.length > 0 && calendar.weekdays.every(n => finiteInt(n, 6)) && new Set(calendar.weekdays).size === calendar.weekdays.length && Array.isArray(calendar.holidays) && calendar.holidays.length < 367 && calendar.holidays.every(validDate);
}
function open(date, calendar) {
  return calendar.weekdays.includes(new Date(date + 'T00:00:00Z').getUTCDay()) && !calendar.holidays.includes(date);
}
function nextOpen(date, calendar) {
  if (!validDate(date) || !calendarValid(calendar)) throw new TypeError('Invalid calendar');
  for (let n = 0; n < 1100; n++) {
    if (open(date, calendar)) return date;
    date = addCalendarDays(date, 1);
  }
  throw new RangeError('No working date within supported horizon');
}
function addWorkingDays(date, count, calendar) {
  if (!finiteInt(count) || !calendarValid(calendar)) throw new TypeError('Invalid working-day rule');
  date = nextOpen(date, calendar);
  for (let n = 0; n < count; n++) date = nextOpen(addCalendarDays(date, 1), calendar);
  return date;
}
function validateConfig(config) {
  const errors = [];
  if (!isPlain(config)) return ['Configuration must be an object'];
  if (config.version !== 1) errors.push('Unsupported configuration version');
  if (typeof config.revision !== 'string' || !config.revision.trim()) errors.push('A rule revision is required');
  try { if (typeof config.timeZone !== 'string' || !config.timeZone.trim()) throw new Error(); civilDate('2026-01-01T00:00:00Z', config.timeZone); } catch { errors.push('A valid IANA time zone is required'); }
  if (!isPlain(config.rules)) errors.push('Rules must be an object');
  for (const [name, rule] of Object.entries(config.rules || {})) {
    if (!isPlain(rule)) { errors.push(`${name}: invalid rule`); continue; }
    if (typeof rule.approved !== 'boolean') errors.push(`${name}: explicit approval flag required`);
    if (!Array.isArray(rule.countries) || rule.countries.some(c => !/^[A-Z]{2}$/.test(c))) errors.push(`${name}: use explicit ISO country codes`);
    if (!finiteInt(rule.cutoffMinute, 1440)) errors.push(`${name}: invalid cutoff`);
    for (const phase of ['processing', 'transit']) {
      const p = rule[phase];
      if (!isPlain(p) || !finiteInt(p.min) || !finiteInt(p.max) || p.min > p.max || !calendarValid(p.calendar)) errors.push(`${name}: invalid ${phase} range/calendar`);
    }
    if (!calendarValid(rule.arrivalCalendar)) errors.push(`${name}: invalid arrival calendar`);
  }
  for (const [profile, countries] of Object.entries(config.profiles || {})) {
    if (!/^[a-zA-Z0-9_-]+$/.test(profile) || !isPlain(countries) || Object.entries(countries).some(([code, name]) => !/^[A-Z]{2}$/.test(code) || !Object.hasOwn(config.rules || {}, name))) errors.push('Invalid profile/country assignment');
  }
  if (config.profiles !== undefined && !isPlain(config.profiles)) errors.push('Profiles must be an object');
  if (config.countryRules !== undefined && (!isPlain(config.countryRules) || Object.entries(config.countryRules).some(([code, name]) => !/^[A-Z]{2}$/.test(code) || (name !== null && !Object.hasOwn(config.rules || {}, name))))) errors.push('Invalid country rule assignment');
  if (config.defaultRule !== null && !Object.hasOwn(config.rules || {}, config.defaultRule)) errors.push('Default rule does not exist');
  for (const type of ['products', 'variants']) {
    if (!isPlain(config[type])) errors.push(`${type}: assignment map required`);
    for (const [id, name] of Object.entries(config[type] || {})) {
      if (!/^\d+$/.test(id) || (name !== null && !Object.hasOwn(config.rules || {}, name) && !Object.hasOwn(config.profiles || {}, name))) errors.push(`${type}: invalid assignment`);
    }
  }
  return errors;
}
function resolveRule(config, { country, productId, variantId } = {}) {
  const errors = validateConfig(config);
  if (errors.length) return { ok: false, reason: 'invalid-config', errors };
  if (config.enabled !== true) return { ok: false, reason: 'disabled' };
  const c = typeof country === 'string' ? country.toUpperCase() : '';
  const variant = String(variantId || '').split('/').pop();
  const product = String(productId || '').split('/').pop();
  const name = Object.hasOwn(config.variants, variant) ? config.variants[variant] : Object.hasOwn(config.products, product) ? config.products[product] : Object.hasOwn(config.countryRules || {}, c) ? config.countryRules[c] : config.defaultRule;
  const resolvedName = Object.hasOwn(config.profiles || {}, name) ? config.profiles[name][c] : name;
  const rule = config.rules[resolvedName];
  if (!rule || rule.approved !== true) return { ok: false, reason: 'unconfirmed-rule' };
  if (!/^[A-Z]{2}$/.test(c)) return { ok: false, reason: 'unknown-country' };
  if (!rule.countries.includes(c) && rule.otherCountriesApproved !== true) return { ok: false, reason: 'unsupported-country' };
  return { ok: true, name: resolvedName, rule, country: c };
}
function estimate(config, context, instant = new Date()) {
  const selected = resolveRule(config, context);
  if (!selected.ok) return selected;
  const {name, rule, country} = selected;
  try {
    const local = civilDate(instant, config.timeZone);
    const basis = local.minute >= rule.cutoffMinute ? addCalendarDays(local.date, 1) : local.date;
    const orderDate = nextOpen(basis, rule.processing.calendar);
    const dispatchMin = addWorkingDays(orderDate, rule.processing.min, rule.processing.calendar);
    const dispatchMax = addWorkingDays(orderDate, rule.processing.max, rule.processing.calendar);
    const arrivalMin = nextOpen(addWorkingDays(dispatchMin, rule.transit.min, rule.transit.calendar), rule.arrivalCalendar);
    const arrivalMax = nextOpen(addWorkingDays(dispatchMax, rule.transit.max, rule.transit.calendar), rule.arrivalCalendar);
    return { ok: true, rule: name, revision: config.revision, country, basisDate: local.date, orderDate, dispatchMin, dispatchMax, arrivalMin, arrivalMax, timeZone: config.timeZone };
  } catch { return { ok: false, reason: 'invalid-date' }; }
}
function formatRange(min, max, locale = 'en-CA') {
  if (!validDate(min) || !validDate(max) || min > max) throw new TypeError('Invalid range');
  const date = (x) => new Date(x + 'T12:00:00Z');
  const fmt = (x, year) => new Intl.DateTimeFormat(locale, {timeZone:'UTC',month:'short',day:'numeric',...(year ? {year:'numeric'} : {})}).format(date(x));
  if (min === max) return fmt(min, true);
  return `${fmt(min, min.slice(0,4) !== max.slice(0,4))} – ${fmt(max, true)}`;
}
function snapshotProperties(existing, result, locale = 'en-CA') {
  if (!result?.ok) return {...(existing || {})};
  return {...(existing || {}), 'Estimated delivery': `${formatRange(result.arrivalMin, result.arrivalMax, locale)} (${result.country} estimate)`, _jd_eta_revision: result.revision, _jd_eta_country: result.country, _jd_eta_basis: result.basisDate, _jd_eta_min: result.arrivalMin, _jd_eta_max: result.arrivalMax};
}
function snapshotChange(line, result, locale = 'en-CA') {
  if (!result?.ok || !line || typeof line.key !== 'string' || !line.key.trim() || !finiteInt(line.quantity, 100000) || line.quantity === 0) return null;
  const properties = snapshotProperties(line.properties, result, locale);
  const existing = line.properties || {};
  if (Object.keys(properties).every(key => properties[key] === existing[key]) && Object.keys(properties).length === Object.keys(existing).length) return null;
  return {id: line.key, quantity: line.quantity, properties};
}
function cartEstimates(cart, config, country, instant) {
  return (cart?.items || []).map(line => ({key: line.key, variantId: line.variant_id, quantity: line.quantity, estimate: line.requires_shipping === false ? {ok:false, reason:'no-shipping'} : estimate(config, {country,productId:line.product_id,variantId:line.variant_id}, instant)}));
}
function editorConfig(raw) {
  if (!raw.editorMode || raw.editorMode === 'observed') return raw;
  const config={...raw,defaultRule:null,countryRules:{},products:{},variants:{},profiles:{},rules:{}};
  if (raw.editorMode === 'unconfigured') return config;
  if (raw.editorMode !== 'profiles' || !Array.isArray(raw.profileRecords)) throw new TypeError('Invalid editor mode');
  const parseMap=value=>{const parsed=JSON.parse(value || '{}');if(!isPlain(parsed))throw new TypeError('Invalid assignment map');return parsed;};
  config.products=parseMap(raw.productAssignments);
  config.variants=parseMap(raw.variantAssignments);
  for (const record of raw.profileRecords) {
    if (!isPlain(record) || !/^[A-Za-z0-9_-]+$/.test(record.profile) || !/^[A-Z]{2}$/.test(record.country)) throw new TypeError('Invalid profile record');
    const id=record.profile+'__'+record.country;
    if(Object.hasOwn(config.rules,id)) throw new TypeError('Duplicate profile country');
    const holidays=String(record.holidays || '').split(',').map(x=>x.trim()).filter(Boolean);
    const calendar=business=>({weekdays:business?[1,2,3,4,5]:[0,1,2,3,4,5,6],holidays});
    config.rules[id]={approved:record.approved===true,countries:[record.country],otherCountriesApproved:false,cutoffMinute:record.cutoffHour*60,processing:{min:record.processingMin,max:record.processingMax,calendar:calendar(record.processingBusiness)},transit:{min:record.transitMin,max:record.transitMax,calendar:calendar(record.transitBusiness)},arrivalCalendar:{weekdays:record.arrivalBusiness?[1,2,3,4,5]:[0,1,2,3,4,5,6],holidays}};
    if(!Object.hasOwn(config.profiles,record.profile)) config.profiles[record.profile]={};
    config.profiles[record.profile][record.country]=id;
  }
  return config;
}

const sameProperties=(a,b)=>Object.keys(a).length===Object.keys(b).length && Object.keys(a).every(k=>a[k]===b[k]);
class EtaBridge {
  constructor({config,country,readCart,changeProperties,now,onCart=()=>{},allowWrites=false}) {
    this.config=config;this.country=country;this.readCart=readCart;this.changeProperties=changeProperties;this.now=now;this.onCart=onCart;this.allowWrites=allowWrites;
    this.pending=new Set();this.generation=0;this.active=null;this.stopped=false;this.stopSignal=new Promise(resolve=>{this.releaseStop=resolve;});
  }
  track(promise) {
    if(!promise?.then) return;
    this.generation++;
    const settled=Promise.resolve(promise).catch(()=>{}).finally(()=>this.pending.delete(settled));
    this.pending.add(settled);
  }
  setCountry(country) {if(country!==this.country){this.country=country;this.generation++;}}
  stop() {this.stopped=true;this.generation++;this.releaseStop();}
  async settled() {
    while(this.pending.size && !this.stopped) await Promise.race([Promise.all([...this.pending]),this.stopSignal]);
  }
  synchronize() {
    if(this.active) return this.active;
    this.active=this.run().finally(()=>{this.active=null;});
    return this.active;
  }
  async run() {
    if(this.allowWrites !== true || this.config.enabled !== true || validateConfig(this.config).length) return {ready:false,reason:'not-authorized',writes:0};
    let writes=0;
    let lastCart=null;
    let oneConflictRetry=false;
    let previousAttempt=null;
    while(!this.stopped) {
      if(this.allowWrites !== true) return {ready:false,reason:'not-authorized',writes,cart:lastCart};
      await this.settled();
      if(this.stopped) return {ready:false,reason:'stopped',writes,cart:lastCart};
      const generation=this.generation;
      const country=this.country;
      const cart=await this.readCart();
      lastCart=cart;
      if(!Array.isArray(cart?.items)) return {ready:false,reason:'invalid-cart',writes};
      if(this.stopped) return {ready:false,reason:'stopped',writes,cart:lastCart};
      if(this.allowWrites !== true) return {ready:false,reason:'not-authorized',writes,cart:lastCart};
      if(generation!==this.generation || this.pending.size) continue;
      const results=cartEstimates(cart,this.config,country,this.now());
      const missing=results.filter(x=>!x.estimate.ok && x.estimate.reason!=='no-shipping');
      if(missing.length) return {ready:false,reason:'unconfirmed-rule',missing,writes,cart};
      let patch=null;
      let intendedLine=null;
      for(const result of results) {
        if(!result.estimate.ok) continue;
        const line=cart.items.find(x=>x.key===result.key);
        if(typeof line?.key!=='string' || !line.key.trim()) return {ready:false,reason:'invalid-line',writes,cart};
        const properties=snapshotProperties(line.properties,result.estimate);
        if(!sameProperties(properties,line.properties||{})) {patch={id:line.key,properties};intendedLine=line;break;}
      }
      if(!patch) {
        await this.onCart(cart);
        if(this.stopped) return {ready:false,reason:'stopped',writes,cart};
        if(this.allowWrites !== true) return {ready:false,reason:'not-authorized',writes,cart:lastCart};
      if(generation!==this.generation || this.pending.size) continue;
        return {ready:true,writes,cart};
      }
      if(this.allowWrites !== true) return {ready:false,reason:'not-authorized',writes,cart:lastCart};
      if(generation!==this.generation || this.pending.size) continue;
      const fingerprint=JSON.stringify(patch);
      if(previousAttempt===fingerprint) return {ready:false,reason:'write-not-verified',writes,cart};
      previousAttempt=fingerprint;
      try {
        const changed=await this.changeProperties(patch);
        writes++;
        lastCart=changed;
        oneConflictRetry=false;
      } catch(error) {
        const fresh=await this.readCart();
        lastCart=fresh;
        if(!Array.isArray(fresh?.items)) return {ready:false,reason:'invalid-cart',writes};
        if(this.stopped) return {ready:false,reason:'stopped',writes,cart:fresh};
        const landed=fresh.items?.some(line=>line.variant_id===intendedLine.variant_id && line.product_id===intendedLine.product_id && sameProperties(line.properties||{},patch.properties));
        if(landed) {writes++;oneConflictRetry=false;continue;}
        const current=fresh.items?.find(line=>line.key===patch.id);
        if(error?.status===422 && !current && !oneConflictRetry) {oneConflictRetry=true;continue;}
        return {ready:false,reason:error?.status===422?'cart-conflict':'cart-write-unconfirmed',writes,cart:fresh};
      }
    }
    return {ready:false,reason:'stopped',writes,cart:lastCart};
  }
}

function ajaxTransport({fetch,root='/'}) {
  if(typeof root!=='string' || !/^\/(?:[A-Za-z0-9_-]+\/)?$/.test(root)) throw new TypeError('Expected locale-aware same-origin root');
  const readCart=async()=>{
    const response=await fetch(root+'cart.js',{credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}});
    if(!response.ok) throw Object.assign(new Error('Cart read failed'),{status:response.status});
    return response.json();
  };
  const changeProperties=async({id,properties})=>{
    const response=await fetch(root+'cart/change.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({id,properties})});
    if(!response.ok) throw Object.assign(new Error('Cart property update failed'),{status:response.status});
    return response.json();
  };
  return {readCart,changeProperties};
}

function redundantSingleItemPrice(item, hasCompareAt=false) {
  return item?.quantity===1 && !hasCompareAt && !item.selling_plan_allocation && !item.unit_price_measurement && !item.has_components && !(item.item_components?.length) && !(item.line_level_discount_allocations?.length) && Number.isInteger(item.original_price) && item.original_price>=0 && item.original_price===item.final_price && item.final_price===item.final_line_price && item.original_line_price===item.original_price;
}
class EtaPreview {
  constructor(root, options = {}) {
    this.root = root;
    this.document = root.ownerDocument;
    this.window = this.document.defaultView;
    this.fetch = options.fetch || this.window.fetch.bind(this.window);
    this.anchor = Number(root.dataset.serverEpoch) * 1000;
    this.anchorTick = this.window.performance.now();
    this.now = options.now || (() => new Date(this.anchor + this.window.performance.now() - this.anchorTick));
    this.controller = new this.window.AbortController();
    this.cartController = null;
    this.sequence = 0;
    this.cart = null;
    this.lines = new Map();
    this.config = editorConfig(JSON.parse(root.querySelector('[data-jd-eta-config]').textContent));
    this.country = root.dataset.country;
    this.locale = root.dataset.locale || 'en-CA';
    this.cleanPreview = root.dataset.cleanPreview==='true' && this.window.Shopify?.theme?.role==='unpublished';
    this.configErrors = validateConfig(this.config);
    const requested=root.dataset.snapshotMode==='true' && root.dataset.appDisabled==='true' && root.dataset.designMode!=='true';
    const appPresent=this.appPresent();
    this.persist=requested && !appPresent;
    this.nativeDisplay=this.persist;
    this.displayOwnership=this.cleanPreview || this.nativeDisplay;
    this.emitCart=options.emitCart || (async(cart)=>{
      if(!cart.items.length) return;
      const {CartLinesUpdateEvent}=await import('@shopify/events');
      this.root.dispatchEvent(new CartLinesUpdateEvent({action:'update',context:'cart',lines:cart.items.map(line=>({id:line.key,quantity:line.quantity})),promise:Promise.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart)}),detail:{source:'jd-eta-native'}}));
    });
    const transport=ajaxTransport({fetch:this.fetch,root:this.window.Shopify?.routes?.root || '/'});
    this.bridge=new EtaBridge({config:this.config,country:this.country,...transport,changeProperties:patch=>{
      if(!this.canPersist()) throw new Error('Snapshot permission or app ownership changed');
      return transport.changeProperties(patch);
    },now:this.now,allowWrites:this.persist});
  }
  connect() {
    const signal = this.controller.signal;
    this.document.addEventListener('shopify:cart:lines-update', event => {
      if(event.detail?.source==='jd-eta-native') return;
      this.bridge.track(event.promise);
      const operation = ++this.sequence;
      this.cartController?.abort();
      this.clearCart();
      if (!event.promise?.then) return;
      event.promise.then(() => {if (operation === this.sequence) this.refreshCart();}).catch(() => {if(operation === this.sequence) this.refreshCart();});
    }, {signal});
    this.document.addEventListener('shopify:cart:view', () => this.refreshCart(), {signal});
    this.document.addEventListener('shopify:product:select', event => {event.promise?.then(() => this.paintProduct()).catch(() => {});}, {signal});
    // Checkout is never intercepted. Date snapshots are background best-effort only.
    this.window.addEventListener('pageshow', () => this.refreshCart(), {signal});
    this.document.addEventListener('visibilitychange', () => {if(!this.document.hidden) this.refreshCart();}, {signal});
    this.observer = new this.window.MutationObserver(records => {
      if (!this.root.isConnected) {this.disconnect();return;}
      const externalChange = records.some(record => !record.target.closest?.('[data-jd-eta-owned]') && [...record.addedNodes,...record.removedNodes].some(node => node.nodeType === 1 && !node.matches?.('[data-jd-eta-owned]')));
      if(externalChange) {this.paintProduct();this.paintCart();}
    });
    this.observer.observe(this.document.body, {childList:true,subtree:true});
    this.timer = this.window.setInterval(() => {if(!this.document.hidden){this.paintProduct();this.paintCart();}},60000);
    this.paintProduct();
    this.refreshCart();
  }
  paintProduct() {
    if(this.controller.signal.aborted) return;
    const template=this.root.querySelector('[data-jd-eta-template]');
    if(!template) return;
    for(const host of this.document.querySelectorAll('[data-jd-eta-placement]')) {
      if(!host.querySelector('[data-jd-eta-panel]')) {host.replaceChildren(template.content.cloneNode(true));}
      const panel=host.querySelector('[data-jd-eta-panel]');
      const productId=host.dataset.productId;
      let variantId=host.dataset.variantId;
      const form=[...this.document.querySelectorAll('product-form-component[data-product-id]')].find(node=>node.dataset.productId===productId && Boolean(node.closest('dialog'))===Boolean(host.closest('dialog')));
      const variant=form?.querySelector('input[name="id"]');
      if(variant?.value) variantId=variant.value;
      const result=estimate(this.config,{country:this.country,productId,variantId},this.now());
      this.lastProductEstimate=result;
      const shown={order:result.ok?formatRange(result.orderDate,result.orderDate,this.locale):'',dispatch:result.ok?formatRange(result.dispatchMin,result.dispatchMax,this.locale):'',arrival:result.ok?formatRange(result.arrivalMin,result.arrivalMax,this.locale):''};
      for(const [name,text] of Object.entries(shown)) {const node=panel.querySelector(`[data-jd-eta-${name}]`);if(node && node.textContent!==text) node.textContent=text;}
      panel.hidden=!result.ok;
      if(this.cleanPreview && form) {
        for(const app of form.querySelectorAll('.sb_ETA')) {
          if(result.ok && !app.hidden) {app.hidden=true;app.dataset.jdEtaProductSuppressed='true';}
          else if(!result.ok && app.dataset.jdEtaProductSuppressed) {app.hidden=false;delete app.dataset.jdEtaProductSuppressed;}
        }
      }
      const status=host.querySelector('[data-jd-eta-status]');
      const notice=result.ok?(this.persist?this.root.dataset.activeNotice:this.root.dataset.previewNotice):this.root.dataset.unavailableNotice;
      if(status && status.textContent!==notice) status.textContent=notice;
      host.dataset.etaState=result.ok?(this.persist?'estimate':'preview'):result.reason;
    }
  }
  async refreshCart() {
    if(this.controller.signal.aborted || !this.config.enabled || this.configErrors.length) return;
    const sequence=++this.sequence;
    this.cartController?.abort();
    this.cartController=new this.window.AbortController();
    try {
      const base=this.window.Shopify?.routes?.root || '/';
      const response=await this.fetch(base+'cart.js',{credentials:'same-origin',cache:'no-store',signal:this.cartController.signal,headers:{Accept:'application/json'}});
      if(!response.ok) throw new Error('Cart unavailable');
      const cart=await response.json();
      const serverDate=Date.parse(response.headers?.get('Date') || '');
      if(Number.isFinite(serverDate)){this.anchor=serverDate;this.anchorTick=this.window.performance.now();}
      if(sequence !== this.sequence || this.controller.signal.aborted) return;
      this.cart=cart;
      this.paintProduct();
      this.paintCart();
      if(this.persist) await this.synchronize();
    } catch(error) {
      if(sequence===this.sequence && error.name!=='AbortError') {this.cart=null;this.clearCart();}
    }
  }
  appPresent() {
    return Boolean(this.document.querySelector('script[src*="cart_update_on_eta"],script[src*="edd_metafields_extension"],[id*="delivery_date_eta_"] .sb_delivery'));
  }
  canPersist() {
    const allowed=this.persist && this.root.dataset.snapshotMode==='true' && this.root.dataset.appDisabled==='true' && this.root.dataset.designMode!=='true' && !this.controller.signal.aborted && !this.appPresent();
    if(!allowed && this.persist) {this.persist=false;this.bridge.allowWrites=false;this.bridge.stop();}
    return allowed;
  }
  async synchronize() {
    if(!this.canPersist()) return {ready:false,reason:'preview-only-or-app-active'};
    const result=await this.bridge.synchronize();
    this.lastSync=result;
    if(result.cart) this.cart=result.cart;
    if(result.ready && result.writes>0 && !this.controller.signal.aborted) await this.emitCart(result.cart);
    return result;
  }
  suppressLegacyEta(scope=this.document) {
    for(const property of scope.querySelectorAll('.cart-items__property')) {
      if(!property.hidden && property.querySelector('dt')?.textContent.trim()==='Estimated delivery:') {property.hidden=true;property.dataset.jdEtaLegacySuppressed='true';}
    }
  }
  restoreLegacyCart(scope=this.document) {
    for(const node of scope.querySelectorAll('[data-jd-eta-legacy-suppressed]')) {node.hidden=false;delete node.dataset.jdEtaLegacySuppressed;}
    for(const node of scope.querySelectorAll('[data-jd-eta-redundant-price]')) {node.hidden=false;delete node.dataset.jdEtaRedundantPrice;}
  }
  clearCart(restoreAll=false) {
    for(const node of this.document.querySelectorAll('[data-jd-eta-cart-preview]')) node.remove();
    this.restoreLegacyCart();
    if(this.nativeDisplay && !restoreAll) this.suppressLegacyEta();
  }
  paintCart() {
    if(this.controller.signal.aborted) return;
    if(!this.cart) {if(this.nativeDisplay)this.clearCart();return;}
    this.lines=new Map(cartEstimates(this.cart,this.config,this.country,this.now()).map(line=>[line.key,line]));
    for(const row of this.document.querySelectorAll('.cart-items__table-row[data-key]')) {
      const line=this.lines.get(row.dataset.key);
      let node=row.querySelector('[data-jd-eta-cart-preview]');
      if(!line?.estimate.ok) {node?.remove();this.restoreLegacyCart(row);if(this.nativeDisplay)this.suppressLegacyEta(row);continue;}
      if(!node) {
        const target=row.querySelector('.cart-items__variants-wrapper');
        if(!target) {if(this.nativeDisplay)this.suppressLegacyEta(row);continue;}
        const template=this.root.querySelector('[data-jd-eta-cart-template]');
        if(!template?.content.firstElementChild) {this.restoreLegacyCart(row);if(this.nativeDisplay)this.suppressLegacyEta(row);continue;}
        node=template.content.firstElementChild.cloneNode(true);
        target.append(node);
      }
      const date=node.querySelector('[data-jd-eta-cart-date]');
      if(!date) {node.remove();this.restoreLegacyCart(row);if(this.nativeDisplay)this.suppressLegacyEta(row);continue;}
      const text=formatRange(line.estimate.arrivalMin,line.estimate.arrivalMax,this.locale);
      if(date.textContent!==text) date.textContent=text;
      if(this.displayOwnership || this.cleanPreview) {
        const unit=row.querySelector('.cart-items__unit-price-wrapper');
        const item=this.cart.items.find(x=>x.key===row.dataset.key);
        if(unit) {
          const duplicate=redundantSingleItemPrice(item,Boolean(unit.querySelector('s,.compare-at-price')) || unit.querySelectorAll('bdi').length!==1);
          if(duplicate && !unit.hidden){unit.hidden=true;unit.dataset.jdEtaRedundantPrice='true';}
          else if(!duplicate && unit.dataset.jdEtaRedundantPrice){unit.hidden=false;delete unit.dataset.jdEtaRedundantPrice;}
        }
        this.suppressLegacyEta(row);
      }
    }
  }
  disconnect() {
    if(this.controller.signal.aborted) return;
    for(const app of this.document.querySelectorAll('[data-jd-eta-product-suppressed]')) {app.hidden=false;delete app.dataset.jdEtaProductSuppressed;}
    this.bridge.stop();this.controller.abort();this.cartController?.abort();this.observer?.disconnect();this.window.clearInterval(this.timer);this.clearCart(true);
  }
}
if (typeof document !== 'undefined') {
  const initialize=()=>{
    for(const root of document.querySelectorAll('[data-jd-eta-root]')) {
      if(root.jdEtaController) continue;
      try {root.jdEtaController=new EtaPreview(root);root.jdEtaController.connect();} catch {root.dataset.etaState='invalid-config';}
    }
  };
  document.addEventListener('shopify:section:load',initialize);
  initialize();
}

})();
